import type { VoiceCommandIntent } from "../logic/voiceCommand";
import {
  createCompanionVoiceFeedback,
  includesCompanionPhrase,
  normalizeCompanionTranscript
} from "./companionVoice";
import audioCompanionTtsScripts from "./audioCompanionTtsScripts.json";

export type AudioCompanionStage =
  | "p1_recommend"
  | "p2_preview"
  | "p2_playing"
  | "p3_answer"
  | "p4_similar"
  | "p5_generating";

export type AudioCompanionProgramId =
  | "tech-morning"
  | "commute-playlist"
  | "market-brief"
  | "vision-pro-weekly"
  | "ai-hardware-daily"
  | "creator-economy"
  | "nvidia-generated";

export type AudioCompanionVoiceIntent = "explain" | "similar" | "generate";

export interface AudioCompanionProgram {
  id: AudioCompanionProgramId;
  title: string;
  subtitle: string;
  description: string;
  category: string;
  durationLabel: string;
  durationSeconds: number;
  audioSrc: string;
  coverSrc: string;
  spokenText: string;
  accent: "violet" | "cyan" | "amber" | "green";
  tags: string[];
}

export interface AudioCompanionDemoState {
  active: true;
  generationIntroFinishedRunId: number;
  generationRunId: number;
  selectedProgramId: AudioCompanionProgramId;
  stage: AudioCompanionStage;
}

export const AUDIO_COMPANION_GREETING = "早上好，通勤路上陪你一起听";

type AudioCompanionTtsProgramMap = Record<AudioCompanionProgramId, {
  audioSrc: string;
  spokenText: string;
}>;

const AUDIO_COMPANION_PROGRAM_TTS =
  audioCompanionTtsScripts.programs as AudioCompanionTtsProgramMap;

export const AUDIO_COMPANION_PROGRAMS: AudioCompanionProgram[] = [
  {
    id: "tech-morning",
    title: "AI 播客 科技早班车",
    subtitle: "Vision Pro 销量回暖",
    description: "用 18 分钟听完 Apple Vision Pro、英伟达财报和 AI 终端动态。",
    category: "AI 播客",
    durationLabel: "18 min",
    durationSeconds: 18 * 60,
    audioSrc: AUDIO_COMPANION_PROGRAM_TTS["tech-morning"].audioSrc,
    coverSrc: "assets/figma/night-ai/audio-companion-covers/tech-morning.webp",
    spokenText: AUDIO_COMPANION_PROGRAM_TTS["tech-morning"].spokenText,
    accent: "violet",
    tags: ["Vision Pro", "英伟达", "AI 终端"]
  },
  {
    id: "commute-playlist",
    title: "清醒通勤歌单",
    subtitle: "轻快电子与城市流行",
    description: "适合早高峰的清醒节奏，过桥和进隧道时也不吵。",
    category: "音乐",
    durationLabel: "28 min",
    durationSeconds: 28 * 60,
    audioSrc: AUDIO_COMPANION_PROGRAM_TTS["commute-playlist"].audioSrc,
    coverSrc: "assets/figma/night-ai/audio-companion-covers/commute-playlist.webp",
    spokenText: AUDIO_COMPANION_PROGRAM_TTS["commute-playlist"].spokenText,
    accent: "cyan",
    tags: ["通勤", "轻快", "不打扰"]
  },
  {
    id: "market-brief",
    title: "开盘前 5 分钟",
    subtitle: "半导体与消费电子速览",
    description: "宏观、芯片、消费电子三条主线，快速补齐今天的市场背景。",
    category: "财经",
    durationLabel: "5 min",
    durationSeconds: 5 * 60,
    audioSrc: AUDIO_COMPANION_PROGRAM_TTS["market-brief"].audioSrc,
    coverSrc: "assets/figma/night-ai/audio-companion-covers/market-brief.webp",
    spokenText: AUDIO_COMPANION_PROGRAM_TTS["market-brief"].spokenText,
    accent: "amber",
    tags: ["开盘前", "半导体", "消费电子"]
  },
  {
    id: "vision-pro-weekly",
    title: "空间计算周报",
    subtitle: "Vision Pro 生态观察",
    description: "从销量、内容生态和开发者反馈，判断空间计算的真实拐点。",
    category: "类似推荐",
    durationLabel: "16 min",
    durationSeconds: 16 * 60,
    audioSrc: AUDIO_COMPANION_PROGRAM_TTS["vision-pro-weekly"].audioSrc,
    coverSrc: "assets/figma/night-ai/audio-companion-covers/vision-pro-weekly.webp",
    spokenText: AUDIO_COMPANION_PROGRAM_TTS["vision-pro-weekly"].spokenText,
    accent: "cyan",
    tags: ["空间计算", "苹果", "生态"]
  },
  {
    id: "ai-hardware-daily",
    title: "AI 硬件 Daily",
    subtitle: "端侧模型和芯片供应链",
    description: "每天一集，把手机、眼镜、座舱和芯片产业链串起来听。",
    category: "类似推荐",
    durationLabel: "12 min",
    durationSeconds: 12 * 60,
    audioSrc: AUDIO_COMPANION_PROGRAM_TTS["ai-hardware-daily"].audioSrc,
    coverSrc: "assets/figma/night-ai/audio-companion-covers/ai-hardware-daily.webp",
    spokenText: AUDIO_COMPANION_PROGRAM_TTS["ai-hardware-daily"].spokenText,
    accent: "green",
    tags: ["端侧 AI", "芯片", "供应链"]
  },
  {
    id: "creator-economy",
    title: "下一代内容入口",
    subtitle: "从播客到空间内容",
    description: "聊 AI 内容生成、创作者工具和新设备会怎样改变媒体消费。",
    category: "类似推荐",
    durationLabel: "21 min",
    durationSeconds: 21 * 60,
    audioSrc: AUDIO_COMPANION_PROGRAM_TTS["creator-economy"].audioSrc,
    coverSrc: "assets/figma/night-ai/audio-companion-covers/creator-economy.webp",
    spokenText: AUDIO_COMPANION_PROGRAM_TTS["creator-economy"].spokenText,
    accent: "violet",
    tags: ["内容生成", "创作者", "媒体"]
  },
  {
    id: "nvidia-generated",
    title: "英伟达财报深度分析",
    subtitle: "AI 产业链影响拆解",
    description: "从 GPU 供需、云厂商资本开支和端侧 AI，分析财报对产业的连锁影响。",
    category: "AI 生成内容",
    durationLabel: "短总结",
    durationSeconds: 36,
    audioSrc: AUDIO_COMPANION_PROGRAM_TTS["nvidia-generated"].audioSrc,
    coverSrc: "assets/figma/night-ai/audio-companion-covers/nvidia-generated.webp",
    spokenText: AUDIO_COMPANION_PROGRAM_TTS["nvidia-generated"].spokenText,
    accent: "green",
    tags: ["英伟达", "财报", "产业影响"]
  }
];

export const AUDIO_COMPANION_P1_PROGRAM_IDS: AudioCompanionProgramId[] = [
  "tech-morning",
  "commute-playlist",
  "market-brief"
];

export const AUDIO_COMPANION_SIMILAR_PROGRAM_IDS: AudioCompanionProgramId[] = [
  "vision-pro-weekly",
  "ai-hardware-daily",
  "creator-economy"
];

export const AUDIO_COMPANION_ANSWER = {
  text: audioCompanionTtsScripts.answer.spokenText,
  audioSrc: audioCompanionTtsScripts.answer.audioSrc
} as const;

export const AUDIO_COMPANION_GENERATION_REPLY =
  "新的播客内容正在生成。";

export const AUDIO_COMPANION_SIMILAR_RECOMMENDATION = {
  text: audioCompanionTtsScripts.similarRecommendation.spokenText,
  audioSrc: audioCompanionTtsScripts.similarRecommendation.audioSrc
} as const;

export const AUDIO_COMPANION_SIMILAR_REPLY = AUDIO_COMPANION_SIMILAR_RECOMMENDATION.text;

export const AUDIO_COMPANION_WAITING_SONG = {
  title: audioCompanionTtsScripts.waitingSong.title,
  artist: audioCompanionTtsScripts.waitingSong.artist,
  audioSrc: audioCompanionTtsScripts.waitingSong.audioSrc,
  licenseUrl: audioCompanionTtsScripts.waitingSong.licenseUrl,
  sourceUrl: audioCompanionTtsScripts.waitingSong.sourceUrl
} as const;

export const AUDIO_COMPANION_GENERATION_STEPS = [
  { id: "collect", label: "正在采集资讯", completeLabel: "资讯采集完成", delayMs: 1500 },
  { id: "outline", label: "正在组织内容", completeLabel: "内容组织完成", delayMs: 3500 },
  { id: "synthesis", label: "正在合成语音", completeLabel: "语音合成完成", delayMs: 6500 }
] as const;

export function createInitialAudioCompanionState(): AudioCompanionDemoState {
  return {
    active: true,
    generationIntroFinishedRunId: 0,
    generationRunId: 0,
    selectedProgramId: "tech-morning",
    stage: "p1_recommend"
  };
}

export function getAudioCompanionProgram(programId: AudioCompanionProgramId) {
  return AUDIO_COMPANION_PROGRAMS.find((program) => program.id === programId) ?? AUDIO_COMPANION_PROGRAMS[0];
}

export function matchAudioCompanionVoiceIntent(transcript: string): AudioCompanionVoiceIntent | null {
  const normalizedTranscript = normalizeCompanionTranscript(transcript);

  if (!normalizedTranscript) {
    return null;
  }

  const asksForExplanation = includesCompanionPhrase(normalizedTranscript, [
    "什么意思",
    "讲的什么意思",
    "这段讲",
    "解释一下",
    "说明一下"
  ]);
  const asksForSimilarPrograms =
    includesCompanionPhrase(normalizedTranscript, ["类似", "相似", "同类", "相近", "同类型", "接近"]) &&
    includesCompanionPhrase(normalizedTranscript, ["播客", "节目", "推荐", "内容", "音频", "可以听", "能听", "听的"]);
  const mentionsNvidiaTopic = includesCompanionPhrase(normalizedTranscript, [
    "英伟达",
    "nvidia",
    "财报"
  ]);
  const asksForGeneration = includesCompanionPhrase(normalizedTranscript, [
    "深入分析",
    "深度分析",
    "重新生成",
    "生成",
    "产业影响",
    "有什么影响",
    "帮我分析"
  ]);

  if (asksForExplanation) {
    return "explain";
  }

  if (asksForSimilarPrograms) {
    return "similar";
  }

  if (
    normalizedTranscript.includes("重新生成") ||
    (asksForGeneration && (mentionsNvidiaTopic || normalizedTranscript.includes("刚刚提到")))
  ) {
    return "generate";
  }

  return null;
}

export function createAudioCompanionVoiceFeedback({
  matched,
  message,
  transcript
}: {
  matched: boolean;
  message: string;
  transcript: string;
}): VoiceCommandIntent {
  return createCompanionVoiceFeedback({
    companionName: "音乐搭子",
    matched,
    message,
    transcript
  });
}
